import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion'; // For smooth page entry
import { useKernel } from './hooks/AIKernel'; // The Symbolic Engine
import './Shortener.css';

import LinkForm from './LinkForm';
import MidInfoBar from './MidInfoBar';
import UrlList from './UrlList';

const readShortenedLinks = () => {
  try {
    const saved = localStorage.getItem('user_shortened_links');
    if (!saved) return [];

    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) {
      throw new Error('Saved link history must be an array.');
    }

    const validLinks = parsed.filter((link) =>
      link
      && typeof link.shortcode === 'string'
      && typeof link.originalLink === 'string'
    );
    if (validLinks.length !== parsed.length) {
      console.warn('[Linkpin] Ignoring invalid entries in saved link history.');
    }
    return validLinks;
  } catch (error) {
    console.warn('[Linkpin] Could not read saved link history.', error);
    return [];
  }
};

const Shortener = () => {
  // --- AI Kernel Integration ---
  const { agentState, reportBehavior } = useKernel();

  // --- Modern State Management & Persistence ---
  // Lazy initialization: React only parses localStorage on the very first render
  const [shortenedLinkList, setShortenedLinkList] = useState(readShortenedLinks);
  const hadLinksOnLoad = useRef(shortenedLinkList.length > 0);

  useEffect(() => {
    try {
      localStorage.setItem('user_shortened_links', JSON.stringify(shortenedLinkList));
    } catch (error) {
      console.warn('[Linkpin] Could not save link history in this browser.', error);
    }
  }, [shortenedLinkList]);

  useEffect(() => {
    if (hadLinksOnLoad.current) {
      reportBehavior('returning_utility_user', 1, 'interest');
      hadLinksOnLoad.current = false;
    }
  }, [reportBehavior]);

  // --- Handler Functions ---
  const populateShortenedLinkList = (linkData) => {
    // Use functional state updates to prevent race conditions instead of .slice()
    setShortenedLinkList((prevList) => [...prevList, linkData]);
  };

  const clearShortenedLinkList = () => {
    setShortenedLinkList([]);
    try {
      localStorage.removeItem('user_shortened_links');
    } catch (error) {
      console.warn('[Linkpin] Could not remove saved link history.', error);
    }
  };

  return (
    <motion.div
        className={`shortener-main ${agentState.theme}`}
        initial={agentState.ui.simplifyAnimations ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: agentState.ui.simplifyAnimations ? 0 : 0.4, ease: "easeOut" }}
    >
      <div className="shortener-heading">
        <span className="shortener-heading__eyebrow">LINK CREATOR</span>
        <h2>Shorten a URL</h2>
        <p>Paste a destination below to create a compact link you can share.</p>
      </div>
      <LinkForm populateLinkList={populateShortenedLinkList} />
      <MidInfoBar clearLinkList={clearShortenedLinkList} linkCount={shortenedLinkList.length} />
      <UrlList shortenedLinkList={shortenedLinkList} />
    </motion.div>
  );
};

export default Shortener;